package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileKind
import com.advaita.cloud.drive.ui.store.SEARCH_MIN_CHARS
import com.advaita.cloud.drive.ui.store.SearchStore
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Spotlight search (ADV-1215): fixed overlay per the app-shell overlay rules.
 * All state/logic lives in [SearchStore]; this renders and routes keys.
 */
@Composable
fun SearchModal(store: SearchStore, onPick: (FileDto) -> Unit) {
    if (!store.isOpen) return
    Div(attrs = {
        classes("search-backdrop")
        onClick { store.close() }
    }) {
        Div(attrs = {
            classes("search-panel")
            onClick { it.stopPropagation() }
        }) {
            // ADV-1413: the input sits alone in its wrapper. A password manager puts its own element
            // next to an input, and Compose adds and removes this panel's children by position. Next
            // to the conditional error, footer and results below, that element made a change remove
            // it instead of the old child, which stayed on screen. The share picker had the same
            // fault (ShareModal.kt, advaita-drive-service #120).
            Div(attrs = { classes("search-input-wrap") }) {
                Input(InputType.Text, attrs = {
                    id("search-input")
                    classes("search-input")
                    placeholder("File or folder name — * and ? match patterns")
                    // Focus explicitly (autoFocus doesn't fire for a dynamically-mounted modal) —
                    // same ref idiom as Modal.kt's inputs.
                    ref { element -> element.focus(); onDispose { } }
                    onInput { store.onQueryChanged(it.value) }
                    onKeyDown { e ->
                        when (e.key) {
                            "ArrowDown" -> { e.preventDefault(); store.moveSelection(1) }
                            "ArrowUp" -> { e.preventDefault(); store.moveSelection(-1) }
                            "Enter" -> store.results.getOrNull(store.activeIndex)?.let(onPick)
                            "Escape" -> store.close()
                        }
                    }
                })
            }
            Div(attrs = { classes("search-scope") }) {
                Text(
                    if (store.sharedFolder == null && store.scopePath == "/") "Searching your whole drive — newest first"
                    else "Searching in ${store.scopeName} — newest first",
                )
            }
            store.error?.let { Div(attrs = { classes("search-error") }) { Text(it) } }
            if (store.moreMayExist) {
                Div(attrs = { classes("search-footer") }) {
                    Text("More results may exist. Keep typing to narrow the search.")
                }
            } else if (store.results.isEmpty() && store.completedQuery == store.query.trim() &&
                store.query.trim().length >= SEARCH_MIN_CHARS
            ) {
                Div(attrs = { classes("search-footer") }) {
                    Text("No matches for ${store.query.trim()} in ${store.scopeName}")
                }
            } else {
                Div(attrs = { classes("search-results") }) {
                    store.results.forEachIndexed { i, f ->
                        SearchResultRow(f, store.locationOf(f), index = i, active = i == store.activeIndex, onPick = onPick)
                    }
                }
            }
            if (store.hasMore && store.error == null && !store.moreMayExist) Div(attrs = { classes("search-footer") }) {
                Text("Showing ${store.results.size} — keep typing to narrow")
            }
        }
    }
    // Keep the keyboard-selected row in view as arrow keys move it.
    LaunchedEffect(store.activeIndex) {
        document.getElementById("search-result-${store.activeIndex}")
            ?.asDynamic()?.scrollIntoView(js("({block: 'nearest'})"))
    }
}

@Composable
private fun SearchResultRow(f: FileDto, location: String, index: Int, active: Boolean, onPick: (FileDto) -> Unit) {
    Div(attrs = {
        attr("id", "search-result-$index")
        classes("search-result")
        if (active) classes("active")
        onClick { onPick(f) }
    }) {
        Span {
            Icon(if (f.kind == FileKind.FOLDER) IconName.FOLDER else IconName.FILE)
            Text(" ${f.displayName}")
        }
        Span(attrs = { classes("meta") }) {
            // "Where this lives" ([SearchStore.locationOf]): the parent folder, and in a shared
            // search the part under the shared root only.
            val date = f.createdAt.take(10)
            val size = f.sizeBytes?.let { " · ${formatSize(it)}" } ?: ""
            Text("$location · $date$size")
        }
    }
}
